import React, { useContext, useEffect, useState } from 'react';
import { PlusIcon, EllipsisVerticalIcon, EyeIcon, PencilIcon } from '@heroicons/react/16/solid';
import { Dropdown, DropdownButton, DropdownItem, DropdownMenu } from '../../../components/Dropdown';
import { Badge } from '../../../components/Badge';
import { Link } from '../../../components/Link';
import { Button } from '../../../components/Button';
import { Heading } from '../../../components/Heading';
import { Divider } from '../../../components/Divider';
import { UserService } from '../../../services/userService'; // Import the UserService
import { Avatar } from '../../../components/Avatar';
import Loading from '../../../components/Loading';
import Pagination from '../../../components/Pagination'; // Import the Pagination component
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogActions, DialogBody, DialogDescription, DialogTitle } from '../../../components/Dialog';
import { useStockAdjust } from '../../../hooks/useStockAdjust';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../components/Table';
import dateFormat from 'dateformat';
import { StockAdjust } from '../../../models/StockAdjust';
import { UserContext } from '../../../context/userContext';
import { useWarehouses } from '../../../hooks/useWarehouse';
import { Text } from '../../../components/Text';
import { Card } from '../../../components/Card';
import { ListToolbarRow, ListToolbarSearch, FiltersButton, SortMenu, ColumnsMenu, isColumnVisible } from '../../../components/ListToolbar';
import { FiltersDialog, FilterPills, FilterPill } from '../../../components/FiltersDialog';
import { DateRangePicker } from '../../../components/DateRangePicker';
import { Select } from '../../../components/Select';

const COLUMNS = [
    { key: 'manualNo', label: 'Manual No' },
    { key: 'customer', label: 'Customer' },
    { key: 'issuer', label: 'Issued by' },
    { key: 'created', label: 'Created on' },
];
const SORT_OPTIONS = [
    { label: 'Newest first', value: 'newest' },
    { label: 'Oldest first', value: 'oldest' },
];

const StockAdjustPage: React.FC = () => {
    const { currentUser } = useContext(UserContext); // Get current user info
    const {
        loading,
        error,
        stockAdjusts,
        totalStockAdjusts,
        fetchStockAdjusts,
        deleteStockAdjust,
    } = useStockAdjust();
    const [searchQuery, setSearchQuery] = useState<string>('');
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [itemsPerPage] = useState<number>(10);
    const [isOpen, setIsOpen] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [selectedStockAdjustId, setSelectedStockAdjustId] = useState<string | null>(null);
    const navigate = useNavigate();
    const { warehouses } = useWarehouses();
    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const [sortOrder, setSortOrder] = useState('newest');
    const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>({});
    const toggleColumn = (key: string) => setColumnVisibility((prev) => ({ ...prev, [key]: prev[key] === false ? true : false }));
    const [filterStartDate, setFilterStartDate] = useState('');
    const [filterEndDate, setFilterEndDate] = useState('');
    const [filterYear, setFilterYear] = useState('');
    const [filterWarehouseId, setFilterWarehouseId] = useState('');

    const resetFilters = () => {
        setFilterStartDate('');
        setFilterEndDate('');
        setFilterYear('');
        setFilterWarehouseId('');
    };

    const availableYears = Array.from(
        new Set(
            (Array.isArray(stockAdjusts) ? stockAdjusts : [])
                .map((s) => new Date(s.created_time).getFullYear())
                .filter((year) => !Number.isNaN(year))
        )
    ).sort((a, b) => b - a);

    const activeFilterCount = [filterStartDate, filterEndDate, filterYear, filterWarehouseId].filter(Boolean).length;

    const filterPills: FilterPill[] = [
        ...(filterStartDate ? [{ key: 'from', label: `From: ${filterStartDate}`, onClear: () => setFilterStartDate('') }] : []),
        ...(filterEndDate ? [{ key: 'to', label: `To: ${filterEndDate}`, onClear: () => setFilterEndDate('') }] : []),
        ...(filterYear ? [{ key: 'year', label: `Year: ${filterYear}`, onClear: () => setFilterYear('') }] : []),
        ...(filterWarehouseId ? [{
            key: 'warehouse',
            label: `Warehouse: ${warehouses?.find((w) => w.id?.toString() === filterWarehouseId)?.warehouse_name || filterWarehouseId}`,
            onClear: () => setFilterWarehouseId(''),
        }] : []),
    ];

    useEffect(() => {
        document.title = "Stock Adjusts";
    }, []);

    // Filter stock adjusts based on the search query
    const filteredStockAdjust = Array.isArray(stockAdjusts)
        ? stockAdjusts?.filter((stockadjust) => {
            const matchesSearch = stockadjust.manual_no?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                stockadjust.reference_no?.toLowerCase().includes(searchQuery.toLowerCase())
            const matchesStartDate = !filterStartDate || new Date(stockadjust.created_time) >= new Date(filterStartDate);
            const matchesEndDate = !filterEndDate || new Date(stockadjust.created_time) <= new Date(filterEndDate);
            const matchesYear = !filterYear || new Date(stockadjust.created_time).getFullYear().toString() === filterYear;
            const matchesWarehouse = !filterWarehouseId || stockadjust.warehouse_id === Number(filterWarehouseId);

            return matchesSearch && matchesStartDate && matchesEndDate && matchesYear && matchesWarehouse;
        }) : [];

    // Open the delete dialog with the selected user's ID
    const handleOpenDeleteDialog = (StockAdjustId: string) => {
        setSelectedStockAdjustId(StockAdjustId);
        setIsOpen(true);
    };

    // Handle delete user
    const handleDelete = async () => {
        if (!selectedStockAdjustId) return;

        setIsDeleting(true);
        try {
            await deleteStockAdjust(Number(selectedStockAdjustId)); // Call the delete API
            fetchStockAdjusts(); // Refresh the user list
            setIsOpen(false);
        } catch (error) {
            console.error('Failed to delete Stock Adjust data:', error);
        } finally {
            setIsDeleting(false);
        }
    };

    return (
        <>
            <div className="flex flex-wrap items-end justify-between gap-4">
                <div className="max-sm:w-full sm:flex-1">
                    <Heading>Stock Adjust</Heading>
                </div>
                <Button onClick={() => navigate('/gp/stocks/stockadjust/add')}><PlusIcon />Create new Stock-adjust</Button>
            </div>

            <Card className="mt-6 p-5">
            <ListToolbarRow search={<ListToolbarSearch value={searchQuery} onChange={setSearchQuery} placeholder="Search stock adjust…" />}>
                <FiltersButton count={activeFilterCount} onClick={() => setIsFilterOpen(true)} />
                <SortMenu options={SORT_OPTIONS} value={sortOrder} onChange={setSortOrder} />
                <ColumnsMenu columns={COLUMNS} visible={columnVisibility} onToggle={toggleColumn} />
            </ListToolbarRow>
            <FilterPills pills={filterPills} onClearAll={resetFilters} />

            <ul>
                {loading && <div className='w-full text-center h-full py-36 justify-center'><Loading /></div>}
                {error && <p className='py-16 text-center text-red-700'>{error}</p>}
                {(!loading && !error) && (
                    <div>
                        <Table className="[--gutter:theme(spacing.6)] lg:[--gutter:theme(spacing.10)]">
                            <TableHead>
                                <TableRow>
                                    <TableHeader>S/N</TableHeader>
                                    <TableHeader>Reference No</TableHeader>
                                    {isColumnVisible(columnVisibility, 'manualNo') && <TableHeader>Manual No</TableHeader>}
                                    {isColumnVisible(columnVisibility, 'customer') && <TableHeader>Customer </TableHeader>}
                                    {isColumnVisible(columnVisibility, 'issuer') && <TableHeader>Issued by</TableHeader>}
                                    {isColumnVisible(columnVisibility, 'created') && <TableHeader>Created on</TableHeader>}
                                    <TableHeader className="text-center">Actions</TableHeader>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {(filteredStockAdjust.length > 0) ? [...filteredStockAdjust].sort((a, b) => {
                                    const diff = new Date(a.created_time).getTime() - new Date(b.created_time).getTime();
                                    return sortOrder === 'newest' ? -diff : diff;
                                }).map((stockAdjust, index) => (
                                    <TableRow key={stockAdjust.id} title={`${stockAdjust.reference_no}`}>
                                        <TableCell>{index + 1}</TableCell>
                                        <TableCell>{stockAdjust.reference_no}</TableCell>
                                        {isColumnVisible(columnVisibility, 'manualNo') && <TableCell className="text-zinc-500">{stockAdjust.manual_no}</TableCell>}
                                        {isColumnVisible(columnVisibility, 'customer') && <TableCell className="text-zinc-500">{stockAdjust.customer_fullname}</TableCell>}
                                        {isColumnVisible(columnVisibility, 'issuer') && <TableCell className="text-zinc-500">{stockAdjust.issuer_fullname}</TableCell>}
                                        {isColumnVisible(columnVisibility, 'created') && <TableCell className="text-zinc-500">{dateFormat(stockAdjust.created_time?.toString(), "mmm dd, yyyy")}</TableCell>}
                                        <TableCell className="py-5 grid grid-cols-3 text-zinc-500">
                                            <a onClick={() => navigate(`/gp/stocks/stockadjust/edit/${stockAdjust.id}`)} className="text-zinc-600 hover:text-zinc-900">
                                                <PencilIcon className='h-5 w-5' />
                                            </a>
                                            <a onClick={() => navigate(`/gp/stocks/stockadjust/view/${stockAdjust.id}`)} className="text-zinc-600 hover:text-zinc-900">
                                                <EyeIcon className='h-5 w-5' />
                                            </a>

                                            <Dropdown>
                                                <DropdownButton plain aria-label="More options" className='h-5 w-5' >
                                                    <EllipsisVerticalIcon />
                                                </DropdownButton>
                                                <DropdownMenu anchor="bottom end">
                                                    <DropdownItem onClick={() => handleOpenDeleteDialog(stockAdjust.id.toString())}>Delete</DropdownItem>
                                                    <DropdownItem onClick={() => navigate(`/gp/stocks/stockadjust/add/items/${stockAdjust.id}`)}>Add Stock-adjust Items</DropdownItem>
                                                </DropdownMenu>
                                            </Dropdown>
                                        </TableCell>
                                    </TableRow>
                                )) : <TableRow>
                                    <TableCell colSpan={9} className="text-center py-8">No stock adjust found.</TableCell>
                                </TableRow>}
                            </TableBody>
                        </Table>
                    </div>
                )}
            </ul>
            </Card>

            <FiltersDialog open={isFilterOpen} onClose={() => setIsFilterOpen(false)} onReset={resetFilters}>
                <div className="sm:col-span-2">
                    <Text>Date Range</Text>
                    <DateRangePicker
                        from={filterStartDate}
                        to={filterEndDate}
                        onChange={({ from, to }) => { setFilterStartDate(from); setFilterEndDate(to); }}
                        className="w-full"
                    />
                </div>
                <div>
                    <Text>By Year</Text>
                    <Select value={filterYear} onChange={(e) => setFilterYear(e.target.value)}>
                        <option value="">All Years</option>
                        {availableYears.map((year) => (
                            <option key={year} value={year.toString()}>{year}</option>
                        ))}
                    </Select>
                </div>
                {(currentUser?.role.toString() === "1") && (
                    <div>
                        <Text>By Warehouse</Text>
                        <Select value={filterWarehouseId} onChange={(e) => setFilterWarehouseId(e.target.value)}>
                            <option value="">All Warehouses</option>
                            {warehouses?.map((warehouse) => (
                                <option key={warehouse.id} value={warehouse.id?.toString()}>
                                    {warehouse.warehouse_name}
                                </option>
                            ))}
                        </Select>
                    </div>
                )}
            </FiltersDialog>

            {/* Delete Confirmation Dialog */}
            <Dialog open={isOpen} onClose={() => setIsOpen(false)}>
                <DialogTitle>Delete Stock Adjust</DialogTitle>
                <DialogDescription>
                    Are you sure you want to delete this Stock Adjust data? This action cannot be undone.
                </DialogDescription>
                <DialogActions>
                    <Button plain onClick={() => setIsOpen(false)} disabled={isDeleting}>
                        Cancel
                    </Button>
                    <Button onClick={handleDelete} disabled={isDeleting}>
                        {isDeleting ? <Loading /> : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default StockAdjustPage;
